<template>
  <section v-if="loading">
    <scroll-container>
      <b-container class="custom-container">
        <b-card>
          <b-skeleton
            width="20%"
            class="mb-1"
          />
          <b-skeleton
            height="35px"
            class="mb-2"
          />
          <b-skeleton
            width="20%"
            class="mb-1"
          />
          <b-skeleton
            height="80px"
            class="mb-2"
          />
          <b-skeleton
            width="20%"
            class="mb-1"
          />
          <b-skeleton
            height="35px"
            class="mb-2"
          />
          <b-skeleton
            width="20%"
            class="mb-1"
          />
          <b-skeleton
            height="35px"
            class="mb-2"
          />
          <b-skeleton
            width="20%"
            class="mb-1"
          />
          <b-row
            class="mb-2"
          >
            <b-col
              v-for="i in 4"
              :key="i"
              md="3"
            >
              <b-skeleton
                :key="i"
                height="90px"
              />
            </b-col>
          </b-row>
          <b-skeleton
            width="40%"
          />
        </b-card>
        <b-skeleton
          type="button"
          class="my-1"
          height="35px"
          width="30%"
        />
        <b-card
          class="mb-1"
        >
          <b-skeleton
            width="100%"
            height="30px"
          />
        </b-card>
      </b-container>
    </scroll-container>
  </section>
  <section v-else>
    <scroll-container>
      <b-container class="custom-container">
        <b-card class="mb-1">
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Forms.Survey.SurveyName') + ' *'"
            :invalid-feedback="wrapper.survey.errors['Name']"
            :state="wrapper.survey.errors['Name'] === undefined ? null : false"
          >
            <b-form-input
              id="Name"
              v-model="wrapper.survey.name"
              type="text"
              :placeholder="$tk('ClientPortal.Forms.Survey.SurveyNamePlaceholder')"
              :state="wrapper.survey.errors['Name'] === undefined ? null : false"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Forms.Survey.Description')"
            :invalid-feedback="wrapper.survey.errors['Description']"
          >
            <b-form-textarea
              id="Description"
              v-model="wrapper.survey.description"
              :placeholder="$tk('ClientPortal.Forms.Survey.DescriptionPlaceholder')"
              :state="wrapper.survey.errors['Description'] === undefined ? null : false"
            />
          </b-form-group>
          <b-form-group
            v-if="accountAccessManager.canManageSurveys()"
            class="mb-2"
          >
            <template v-slot:label>
              {{ $tk('ClientPortal.Forms.Survey.EndImageDestinationURL') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.EndImageDestinationURLInfo')"
                name="Info"
              />
            </template>
            <b-form-input
              id="EndImageDestinationURL"
              v-model="wrapper.survey.endImageDestinationURL"
              type="url"
              :placeholder="$tk('ClientPortal.Forms.Survey.EndImageDestinationURLPlaceholder')"
              :state="wrapper.survey.errors['EndImageDestinationURL'] === undefined ? null : false"
              :invalid-feedback="wrapper.survey.errors['EndImageDestinationURL']"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Form.Survey.SurveyPrivacyType') + ' *'"
          >
            <v-select
              v-model="wrapper.survey.surveyPrivacyType"
              :clearable="false"
              label="text"
              :placeholder="$tk('ClientPortal.Form.ChoosePrivacyType')"
              :options="wrapper.privacyStatuses"
              :reduce="format => format.value"
              @input="clearSurveySharedAccounts()"
            />
          </b-form-group>
          <AccountsInput
            v-if="wrapper.survey.surveyPrivacyType == 2"
            id="SurveySharedAccounts"
            :accounts="availableAccounts"
            :error-message="wrapper.survey.errors['SurveySharedAccounts']"
            :has-selected-accounts="wrapper.survey.surveySharedAccounts.length > 0"
            @search="searchAccounts"
            @add="addSurveyPrivacyAccount"
          />
          <SelectedItems
            v-if="wrapper.survey.surveyPrivacyType == 2 && wrapper.survey.surveySharedAccounts.length"
            :items="wrapper.survey.surveySharedAccounts"
            id-label="accountId"
            variant="avatar"
            @remove="removeSurveySharedItem"
          />
          <b-form-row
            class="mb-2"
          >
            <b-col
              :sm="wrapper.survey.logoImageUrl != null ? 11 : 12"
            >
              <b-form-group
                class="mb-0"
                label="Logo"
              >
                <b-form-file
                  v-model="wrapper.survey.logo"
                  class="survey__wrapper"
                  :placeholder="getLogoFileName"
                  :drop-placeholder="$tk('ClientPortal.Forms.Survey.DropFilePlaceHolder')"
                  @input="addSurveyAccountLogo"
                />
              </b-form-group>
            </b-col>
            <b-col
              v-if="wrapper.survey.logoImageUrl != null"
              sm="1"
              class="pt-50 pt-sm-2 d-flex justify-content-around flex-row-reverse flex-sm-row"
            >
              <div
                class="mt-1 d-inline-block cursor-pointer"
                @click="showLogo = !showLogo"
              >
                <GjIcon
                  v-if="!showLogo"
                  :key="'show'"
                  name="Show"
                />
                <GjIcon
                  v-else
                  :key="'hide'"
                  name="Hide"
                />
              </div>
              <div
                class="mt-1 d-inline-block cursor-pointer"
                @click="removeLogo"
              >
                <GjIcon
                  name="Delete"
                />
              </div>
            </b-col>
            <b-col
              v-if="wrapper.survey.logoImageUrl != null"
              sm="12"
            >
              <div
                v-show="showLogo"
                class="mt-2"
              >
                <img
                  class="w-100 display-ad-preview-img"
                  :src="wrapper.survey.logoImageUrl"
                >
              </div>
            </b-col>
          </b-form-row>
          <b-form-group ref="themeFormGroup">
            <label>
              {{ $tk('ClientPortal.Forms.Survey.Theme') + ' *' }}
              <GjIcon
                v-b-tooltip.hover="customizeColors ? 'Theme selection is disabled when color customization is enabled' : $tk('ClientPortal.Forms.Survey.ThemeInfo')"
                name="Info"
              />
            </label>
            <b-row
              class="mx-n25"
            >
              <b-col
                v-for="theme in wrapper.themes.filter(x=> x.hidden === false)"
                :key="theme.id"
                class="px-25"
                sm="3"
              >
                <b-media
                  type="button"
                  class="text-center border mb-50 py-1 rounded"
                  :class="[getSurveyThemeClass(theme.id), { 'opacity-50 cursor-not-allowed': customizeColors }]"
                  @click="!customizeColors && setSurveyTheme(theme.id)"
                >
                  <GjIcon
                    :key="theme.id"
                    class="rounded-sm mb-50"
                    :style="'backgroundColor:' + theme.backgroundColor + '; color:' + theme.titleColor + '; border: 1px solid ' + theme.titleColor"
                    name="List_fill"
                    size="40px"
                  />
                  <b-card-text>
                    {{ theme.name }}
                  </b-card-text>
                </b-media>
              </b-col>
            </b-row>
            <span
              v-if="themeText"
              class="text-danger"
            >
              {{ $tk('Error.ThemeIsRequired') }}
            </span>
          </b-form-group>

          <!-- Color Customization Section -->
          <b-row class="mb-2">
            <b-col sm="8">
              <b-card-text class="mb-50">
                {{ $tk('ClientPortal.Form.CustomizeColors') }}
              </b-card-text>
            </b-col>
            <b-col
              sm="4"
              class="text-right"
            >
              <input
                v-model="customizeColors"
                type="hidden"
                name="customizeColors"
              >
              <b-form-checkbox
                v-model="customizeColors"
                class="d-block"
                checked="false"
                name="check-button"
                switch
                @change="onCustomizeColorsToggle"
              />
            </b-col>
          </b-row>

          <b-row
            v-if="customizeColors"
            class="mt-2 mb-2"
          >
            <b-col sm="6">
              <b-form-group>
                <template slot="label">
                  Background Color
                  <GjIcon
                    v-b-tooltip.hover.html="getColorGuideTooltip('background-color')"
                    name="Info"
                    class="color-guide-icon"
                  />
                </template>
                <b-input-group>
                  <b-input-group-prepend>
                    <v-swatches
                      v-model="surveyColors.backgroundColor"
                      :swatches="customSwatches"
                      show-fallback
                      show-border
                      popover-to="right"
                    />
                  </b-input-group-prepend>
                  <v-swatches
                    v-model="surveyColors.backgroundColor"
                    :swatches="customSwatches"
                    show-fallback
                    show-border
                    popover-to="right"
                  >
                    <input
                      slot="trigger"
                      :value="surveyColors.backgroundColor"
                      class="form__input__element"
                      readonly
                    >
                  </v-swatches>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template slot="label">
                  Title Color
                  <GjIcon
                    v-b-tooltip.hover.html="getColorGuideTooltip('title-color')"
                    name="Info"
                    class="color-guide-icon"
                  />
                </template>
                <b-input-group>
                  <b-input-group-prepend>
                    <v-swatches
                      v-model="surveyColors.titleColor"
                      :swatches="customSwatches"
                      show-fallback
                      show-border
                      popover-to="right"
                    />
                  </b-input-group-prepend>
                  <v-swatches
                    v-model="surveyColors.titleColor"
                    :swatches="customSwatches"
                    show-fallback
                    show-border
                    popover-to="right"
                  >
                    <input
                      slot="trigger"
                      :value="surveyColors.titleColor"
                      class="form__input__element"
                      readonly
                    >
                  </v-swatches>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template slot="label">
                  Option Text Color
                  <GjIcon
                    v-b-tooltip.hover.html="getColorGuideTooltip('option-text-color')"
                    name="Info"
                    class="color-guide-icon"
                  />
                </template>
                <b-input-group>
                  <b-input-group-prepend>
                    <v-swatches
                      v-model="surveyColors.optionTextColor"
                      :swatches="customSwatches"
                      show-fallback
                      show-border
                      popover-to="right"
                    />
                  </b-input-group-prepend>
                  <v-swatches
                    v-model="surveyColors.optionTextColor"
                    :swatches="customSwatches"
                    show-fallback
                    show-border
                    popover-to="right"
                  >
                    <input
                      slot="trigger"
                      :value="surveyColors.optionTextColor"
                      class="form__input__element"
                      readonly
                    >
                  </v-swatches>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template slot="label">
                  Option Background Color
                  <GjIcon
                    v-b-tooltip.hover.html="getColorGuideTooltip('option-background-color')"
                    name="Info"
                    class="color-guide-icon"
                  />
                </template>
                <b-input-group>
                  <b-input-group-prepend>
                    <v-swatches
                      v-model="surveyColors.optionBackgroundColor"
                      :swatches="customSwatches"
                      show-fallback
                      show-border
                      popover-to="right"
                    />
                  </b-input-group-prepend>
                  <v-swatches
                    v-model="surveyColors.optionBackgroundColor"
                    :swatches="customSwatches"
                    show-fallback
                    show-border
                    popover-to="right"
                  >
                    <input
                      slot="trigger"
                      :value="surveyColors.optionBackgroundColor"
                      class="form__input__element"
                      readonly
                    >
                  </v-swatches>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template slot="label">
                  Primary Button Text Color
                  <GjIcon
                    v-b-tooltip.hover.html="getColorGuideTooltip('primary-button-text-color')"
                    name="Info"
                    class="color-guide-icon"
                  />
                </template>
                <b-input-group>
                  <b-input-group-prepend>
                    <v-swatches
                      v-model="surveyColors.primaryButtonTextColor"
                      :swatches="customSwatches"
                      show-fallback
                      show-border
                      popover-to="right"
                    />
                  </b-input-group-prepend>
                  <v-swatches
                    v-model="surveyColors.primaryButtonTextColor"
                    :swatches="customSwatches"
                    show-fallback
                    show-border
                    popover-to="right"
                  >
                    <input
                      slot="trigger"
                      :value="surveyColors.primaryButtonTextColor"
                      class="form__input__element"
                      readonly
                    >
                  </v-swatches>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template slot="label">
                  Primary Button Background Color
                  <GjIcon
                    v-b-tooltip.hover.html="getColorGuideTooltip('primary-button-background-color')"
                    name="Info"
                    class="color-guide-icon"
                  />
                </template>
                <b-input-group>
                  <b-input-group-prepend>
                    <v-swatches
                      v-model="surveyColors.primaryButtonBackgroundColor"
                      :swatches="customSwatches"
                      show-fallback
                      show-border
                      popover-to="right"
                    />
                  </b-input-group-prepend>
                  <v-swatches
                    v-model="surveyColors.primaryButtonBackgroundColor"
                    :swatches="customSwatches"
                    show-fallback
                    show-border
                    popover-to="right"
                  >
                    <input
                      slot="trigger"
                      :value="surveyColors.primaryButtonBackgroundColor"
                      class="form__input__element"
                      readonly
                    >
                  </v-swatches>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template slot="label">
                  Secondary Button Text Color
                  <GjIcon
                    v-b-tooltip.hover.html="getColorGuideTooltip('secondary-button-text-color')"
                    name="Info"
                    class="color-guide-icon"
                  />
                </template>
                <b-input-group>
                  <b-input-group-prepend>
                    <v-swatches
                      v-model="surveyColors.secondaryButtonTextColor"
                      :swatches="customSwatches"
                      show-fallback
                      show-border
                      popover-to="right"
                    />
                  </b-input-group-prepend>
                  <v-swatches
                    v-model="surveyColors.secondaryButtonTextColor"
                    :swatches="customSwatches"
                    show-fallback
                    show-border
                    popover-to="right"
                  >
                    <input
                      slot="trigger"
                      :value="surveyColors.secondaryButtonTextColor"
                      class="form__input__element"
                      readonly
                    >
                  </v-swatches>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template slot="label">
                  Secondary Button Background Color
                  <GjIcon
                    v-b-tooltip.hover.html="getColorGuideTooltip('secondary-button-background-color')"
                    name="Info"
                    class="color-guide-icon"
                  />
                </template>
                <b-input-group>
                  <b-input-group-prepend>
                    <v-swatches
                      v-model="surveyColors.secondaryButtonBackgroundColor"
                      :swatches="customSwatches"
                      show-fallback
                      show-border
                      popover-to="right"
                    />
                  </b-input-group-prepend>
                  <v-swatches
                    v-model="surveyColors.secondaryButtonBackgroundColor"
                    :swatches="customSwatches"
                    show-fallback
                    show-border
                    popover-to="right"
                  >
                    <input
                      slot="trigger"
                      :value="surveyColors.secondaryButtonBackgroundColor"
                      class="form__input__element"
                      readonly
                    >
                  </v-swatches>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group
            class="mb-1"
          >
            <div>
              <b-form-checkbox
                v-model="wrapper.survey.showQuestionsNumber"
                class="d-inline m-25"
              >
                {{ $tk('ClientPortal.Forms.Survey.ShowQuestionsNumber') }}
              </b-form-checkbox>
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.ShowQuestionsNumberInfo')"
                name="Info"
              />
            </div>
          </b-form-group>
          <b-form-group
            class="mb-1"
          >
            <div>
              <b-form-checkbox
                v-model="wrapper.survey.hasClickableLink"
                switch
                @change="resetClickableLink"
              >
                {{ $tk('ClientPortal.Forms.Survey.EnableURLLink') }}
              </b-form-checkbox>
            </div>
          </b-form-group>
          <template
            v-if="wrapper.survey.hasClickableLink"
          >
            <b-form-group
              class="mb-2"
              :invalid-feedback="wrapper.survey.errors['ButtonText']"
              :state="wrapper.survey.errors['ButtonText'] === undefined ? null : false"
            >
              <template slot="label">
                {{ $tk('ClientPortal.Forms.Survey.ButtonText') + ' *' }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.ButtonTextLabel')"
                  name="Info"
                />
              </template>
              <b-form-input
                id="ButtonText"
                v-model="wrapper.survey.buttonText"
                type="text"
                :placeholder="$tk('ClientPortal.Forms.Survey.ButtonTextPlaceholder')"
                :state="wrapper.survey.errors['ButtonText'] === undefined ? null : false"
              />
            </b-form-group>
            <b-form-group
              class="mb-2"
              :invalid-feedback="wrapper.survey.errors['DestinationURL']"
              :state="wrapper.survey.errors['DestinationURL'] === undefined ? null : false"
            >
              <template
                slot="label"
              >
                {{ $tk('ClientPortal.Forms.Survey.DestinationURL') + ' *' }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.PleaseEnterAValidURL')"
                  name="Info"
                />
              </template>
              <b-form-input
                id="destinationURL"
                v-model="wrapper.survey.destinationURL"
                type="url"
                :placeholder="$tk('ClientPortal.Forms.Survey.DestinationURLPlaceholder')"
                :state="wrapper.survey.errors['DestinationURL'] === undefined ? null : false"
              />
            </b-form-group>
          </template>
          <b-form-group
            class="mb-1"
          >
            <div>
              <b-form-checkbox
                v-model="wrapper.survey.hasBrandInfoEnabled"
                switch
              >
                {{ $tk('ClientPortal.Forms.Survey.EnableBrandInfo') }}
              </b-form-checkbox>
            </div>
          </b-form-group>
          <template
            v-if="wrapper.survey.hasBrandInfoEnabled"
          >
            <b-form-group
              class="mb-2"
              :invalid-feedback="wrapper.survey.errors['BrandName']"
              :state="wrapper.survey.errors['BrandName'] === undefined ? null : false"
            >
              <template slot="label">
                {{ $tk('ClientPortal.Forms.Survey.ButtonName') + ' *' }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.ButtonNameLabel')"
                  name="Info"
                />
              </template>
              <b-form-input
                id="BrandName"
                v-model="wrapper.survey.brandName"
                type="text"
                :placeholder="$tk('ClientPortal.Forms.Survey.BrandNamePlaceholder')"
                :state="wrapper.survey.errors['BrandName'] === undefined ? null : false"
              />
            </b-form-group>
            <b-form-group
              class="mb-2"
              :invalid-feedback="wrapper.survey.errors['BrandText']"
              :state="wrapper.survey.errors['BrandText'] === undefined ? null : false"
            >
              <template
                slot="label"
              >
                {{ $tk('ClientPortal.Forms.Survey.BrandText') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.BrandTextLabel')"
                  name="Info"
                />
              </template>
              <b-form-input
                id="BrandText"
                v-model="wrapper.survey.brandText"
                type="text"
                :placeholder="$tk('ClientPortal.Forms.Survey.BrandTextPlaceholder')"
                :state="wrapper.survey.errors['BrandText'] === undefined ? null : false"
              />
            </b-form-group>
            <b-form-group
              class="mb-2"
              :invalid-feedback="wrapper.survey.errors['BrandUrl']"
              :state="wrapper.survey.errors['BrandUrl'] === undefined ? null : false"
            >
              <template
                slot="label"
              >
                {{ $tk('ClientPortal.Forms.Survey.BrandUrl') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.PleaseEnterAValidURL')"
                  name="Info"
                />
              </template>
              <b-form-input
                id="BrandUrl"
                v-model="wrapper.survey.brandUrl"
                type="text"
                :placeholder="$tk('ClientPortal.Forms.Survey.BrandUrlPlaceholder')"
                :state="wrapper.survey.errors['BrandUrl'] === undefined ? null : false"
              />
            </b-form-group>
          </template>
          <b-form-group class="mb-1">
            <div>
              <b-form-checkbox
                v-model="wrapper.survey.hasRewardCode"
                switch
                @change="resetRewardCode"
              >
                {{ $tk('ClientPortal.Forms.Survey.EnableRewardCode') }}
              </b-form-checkbox>
            </div>
          </b-form-group>
          <template v-if="wrapper.survey.hasRewardCode">
            <b-form-group
              class="mb-2"
              :invalid-feedback="wrapper.survey.errors['RewardCode']"
              :state="wrapper.survey.errors['RewardCode'] === undefined ? null : false"
            >
              <template
                slot="label"
              >
                {{ $tk('ClientPortal.Forms.Survey.RewardCode') + ' *' }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.RewardCodeLabel')"
                  name="Info"
                />
              </template>
              <b-form-input
                id="RewardCode"
                v-model="wrapper.survey.rewardCode"
                type="text"
                :placeholder="$tk('ClientPortal.Forms.Survey.RewardCodePlaceholder')"
                :state="wrapper.survey.errors['RewardCode'] === undefined ? null : false"
              />
            </b-form-group>
            <b-form-group
              class="mb-2"
              :invalid-feedback="wrapper.survey.errors['RewardMessage']"
              :state="wrapper.survey.errors['RewardMessage'] === undefined ? null : false"
            >
              <template slot="label">
                {{ $tk('ClientPortal.Forms.Survey.RewardMessage') + ' *' }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.UseRewardCodeInMessagePlaceholderBySimplyAdding') + ' {{RewardCode}}'"
                  name="Info"
                />
              </template>
              <b-form-textarea
                id="RewardMessage"
                v-model="wrapper.survey.rewardMessage"
                :placeholder="$tk('ClientPortal.Forms.Survey.RewardMessagePlaceholder')"
                :state="wrapper.survey.errors['RewardMessage'] === undefined ? null : false"
                rows="3"
              />
              <small class="text-muted">{{ wrapper.survey.rewardMessage ? wrapper.survey.rewardMessage.length : 0 }}/500</small>
            </b-form-group>
            <b-form-group class="mb-1">
              <div>
                <b-form-checkbox
                  v-model="wrapper.survey.isRewardUnique"
                  switch
                >
                  {{ $tk('ClientPortal.Forms.Survey.IsRewardUnique') }}
                  <GjIcon
                    v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.IsRewardUniqueLabel')"
                    name="Info"
                  />
                </b-form-checkbox>
              </div>
            </b-form-group>
          </template>
        </b-card>
        <b-card
          v-for="(question, questionIndex) in wrapper.survey.questions"
          :key="questionIndex"
          class="mb-1"
          no-body
        >
          <b-tabs
            nav-class="px-2 mb-0"
            content-class="p-2"
          >
            <b-tab
              :title="$tk('ClientPortal.Forms.Survey.QuestionAndAnswers')"
              active
            >
              <b-form-row
                class="mb-2"
              >
                <b-col
                  sm="7"
                >
                  <b-form-group
                    class="mb-0"
                    :invalid-feedback="question.errors['Name']"
                  >
                    <legend class="col-form-label pt-0">
                      <b-form-row>
                        <b-col>
                          {{ $tk('ClientPortal.Forms.Survey.Question') }} {{ questionIndex + 1 }}
                        </b-col>
                        <b-col
                          class="pt-25 text-right"
                        >
                          <small
                            class="text-muted"
                          >
                            {{ question.name.length }}/{{ configuration.surveyConfiguration.questionMaxLength }}
                          </small>
                        </b-col>
                      </b-form-row>
                    </legend>
                    <b-form-input
                      id="Name"
                      v-model="question.name"
                      type="text"
                      :placeholder="$tk('ClientPortal.Forms.Survey.QuestionNamePlaceholder')"
                      :maxlength="configuration.surveyConfiguration.questionMaxLength"
                      :state="question.errors['Name'] === undefined ? null : false"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  class="pt-35px"
                  sm="4"
                >
                  <b-form-group
                    class="mb-0 w-100"
                    :invalid-feedback="question.errors['Type']"
                    :state="question.errors['Type'] === undefined ? null : false"
                  >
                    <v-select
                      id="Type"
                      v-model="question.type"
                      :clearable="false"
                      label="text"
                      :placeholder="$tk('ClientPortal.Form.ChooseQuestionType')"
                      :options="wrapper.questionTypes"
                      :disabled="question.id > 0"
                      :reduce="format => format.value"
                      @option:selected="(selectedOption, name) => changeSurveyQuestionType(selectedOption.value, question)"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  class="d-flex justify-content-sm-around flex-row-reverse flex-sm-row pt-35px pb-50"
                  sm="1"
                >
                  <b-button
                    size="sm"
                    class="mt-50"
                    variant="link"
                    @click="removeSurveyQuestion(questionIndex, wrapper.survey)"
                  >
                    <GjIcon
                      class="cursor-pointer"
                      name="Delete"
                    />
                  </b-button>
                </b-col>
              </b-form-row>
              <span
                v-if="question.errors"
                class="field-validation-valid text-danger small mb-3"
              >
                {{ question.errors['Answers'] }}
              </span>
              <hr>
              <b-row
                v-if="question.type == 3 && question.id == 0"
                class="mb-2"
              >
                <b-col
                  cols="5"
                >
                  <v-select
                    v-model="question.ratingScaleOption"
                    :clearable="false"
                    label="name"
                    :placeholder="$tk('ClientPortal.Form.ChooseRating')"
                    :options="ratingScaleOptions"
                    :disabled="question.id > 0"
                    :reduce="format => format.id"
                    @option:selected="(selectedOption, name) => changeRatingScaleOption(selectedOption.id, question)"
                  />
                </b-col>
              </b-row>

              <template
                v-if="question.type != 3"
              >
                <b-form-row
                  v-for="(answer, answerIndex) in question.answers"
                  :key="answerIndex"
                  class="mb-2"
                >
                  <b-col
                    v-if="question.type != 3"
                    class="d-none d-sm-flex justify-content-around pt-35px pb-50"
                    sm="1"
                  >
                    <GjIcon
                      v-if="question.type == 1"
                      :key="'record'"
                      class="mt-50"
                      name="Record"
                    />
                    <GjIcon
                      v-else
                      :key="'stop'"
                      class="mt-50"
                      name="Stop"
                    />
                  </b-col>
                  <b-col
                    v-if="question.type != 3"
                    sm="10"
                  >
                    <b-form-group
                      class="mb-0"
                      :invalid-feedback="answer.errors['Answer']"
                    >
                      <legend
                        class="col-form-label pt-0"
                      >
                        <b-form-row>
                          <b-col>
                            {{ $tk('ClientPortal.Forms.Survey.AnswerOption') }} {{ answerIndex + 1 }}
                          </b-col>
                          <b-col
                            class="pt-25 text-right"
                          >
                            <b-card-text
                              class="text-muted"
                            >
                              {{ answer.answer.length }}/{{ configuration.surveyConfiguration.answerMaxLength }}
                            </b-card-text>
                          </b-col>
                        </b-form-row>
                      </legend>
                      <b-form-input
                        id="Answer"
                        v-model="answer.answer"
                        :maxlength="configuration.surveyConfiguration.answerMaxLength"
                        :state="answer.errors['Answer'] === undefined ? null : false"
                        type="text"
                        :placeholder="$tk('ClientPortal.Forms.Survey.AnswerOptionPlaceholder')"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    v-if="question.type != 3"
                    class="d-flex justify-content-sm-around flex-row-reverse flex-sm-row pt-35px pb-50"
                    sm="1"
                  >
                    <b-button
                      size="sm"
                      variant="link"
                      @click="removeSurveyQuestionAnswer(answerIndex, question, questionIndex)"
                    >
                      <GjIcon
                        class="cursor-pointer mt-50"
                        name="Delete"
                      />
                    </b-button>
                  </b-col>
                </b-form-row>
              </template>
              <template v-else>
                <b-row
                  v-for="(answer, answerIndex) in question.answers.filter(x => x.answer == 1 || x.answer == question.answers.length)"
                  :key="answerIndex"
                  class="mb-2"
                >
                  <b-col
                    sm="2"
                  >
                    <b-form-input
                      v-model="answer.answer"
                      :disabled="true"
                      :placeholder="$tk('ClientPortal.Survey.Form.WriteAnswerOptionHere')"
                      :maxlength="configuration.surveyConfiguration.answerMaxLength"
                      class="mb-50 mb-sm-0"
                    />
                  </b-col>
                  <b-col
                    sm="10"
                  >
                    <b-form-input
                      v-model="answer.description"
                      :placeholder="$tk('ClientPortal.Survey.Description')"
                    />
                  </b-col>
                </b-row>
              </template>
              <b-row
                v-if="question.type > 0 && (question.type != 4 && question.type != 3)"
              >
                <b-col
                  offset-sm="1"
                >
                  <b-link @click="addSurveyQuestionAnswer(question)">
                    <GjIcon
                      size="16"
                      name="Plus"
                    />
                    <span
                      class="create-text"
                    >{{ $tk('ClientPortal.Forms.Survey.AddAnswer') }}
                    </span>
                  </b-link>
                </b-col>
              </b-row>
            </b-tab>
            <b-tab :title="$tk('ClientPortal.Forms.Survey.Logic')">
              <b-form-checkbox
                v-if="false"
                v-model="question.allowOtherAnswers"
                checked="false"
                name="check-button"
                switch
                inline
              >
                {{ $tk('ClientPortal.Forms.Survey.AllowOtherAnswer') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.AllowOtherAnswerInfo')"
                  name="Info"
                />
              </b-form-checkbox>
              <!-- <div>
                <b-form-checkbox
                  v-model="question.skippable"
                  checked="false"
                  name="check-button"
                  class="d-inline m-25"
                  switch
                  inline
                >
                  {{ $tk('ClientPortal.Forms.Survey.Skippable') }}
                </b-form-checkbox>
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.SkippableInfo')"
                  name="Info"
                />
              </div> -->
              <!-- <div class="mt-2"> -->
              <div>
                <b-form-checkbox
                  v-model="question.randomizedAnswers"
                  class="d-inline m-25"
                  checked="false"
                  name="check-button"
                  switch
                  inline
                >
                  {{ $tk('ClientPortal.Forms.Survey.RandomizedAnswers') }}
                </b-form-checkbox>
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.RandomizedAnswersInfo')"
                  name="Info"
                />
              </div>
              <!-- /* */ -->
              <template v-if="questionIndex > 0 && question.type > 0">
                <div
                  class="mt-2"
                >
                  <b-form-checkbox
                    v-model="question.dependsOnAnswers"
                    checked="false"
                    name="check-button"
                    switch
                    class="d-inline m-25"
                    inline
                    @change="removeAllSurveyQuestionConditionMapping(question)"
                  >
                    {{ $tk('ClientPortal.Forms.Survey.DependsOnAnswers') }}
                  </b-form-checkbox>
                  <GjIcon
                    v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.DependsOnAnswersInfo')"
                    name="Info"
                  />
                </div>
                <b-form-row
                  v-if="question.dependsOnAnswers"
                  class="mt-2"
                >
                  <b-col
                    sm="4"
                    class="mt-50 mt-sm-0"
                  >
                    <v-select
                      v-model="question.conditionalOperator"
                      :clearable="false"
                      label="name"
                      class="truncate-text smaller-indicator"
                      :placeholder="$tk('ClientPortal.Survey.ChooseOperator')"
                      :options="wrapper.conditionOperators"
                      :reduce="operator => operator.id"
                    />
                  </b-col>
                  <b-col
                    v-if="question.conditionalOperator > 0"
                    sm="8"
                  >
                    <multiselect
                      :options="getQuestionConditionMappingOptions(question, questionIndex)"
                      label="text"
                      track-by="text"
                      :group-select="false"
                      :multiple="false"
                      roup-label="label"
                      group-values="options"
                      select-label=""
                      deselect-label=""
                      :placeholder="$tk('ClientPortal.Survey.ChooseMapping')"
                      @select="(selectedOption, id) => addQuestionConditionMapping(selectedOption.value, question)"
                    >
                      <template
                        slot="noOptions"
                      >
                        {{ $tk('ClientPortal.Form.NoMatchingOptions') }}
                      </template>
                      <template
                        slot="caret"
                        slot-scope="{ toggle }"
                      >
                        <div
                          class="multiselect__select"
                          @mousedown.prevent.stop="toggle()"
                        >
                          <GjIcon
                            class="icon-wrapper"
                            name="ArrowDown_fill"
                            size="21"
                          />
                        </div>
                      </template>
                    </multiselect>
                  </b-col>
                </b-form-row>
              </template>

              <b-form-group
                v-if="question.conditionMappings != null && question.dependsOnAnswers"
                class="mb-0"
              >
                <div
                  v-for="(answer, answerIndex) in question.conditionMappings"
                  :key="answerIndex"
                  class="d-block w-100"
                >
                  <b-row
                    class="mt-2"
                  >
                    <b-col
                      sm="6"
                    >
                      <span class="font-weight-medium">{{ $tk('ClientPortal.Forms.Survey.Question') }}:</span>
                      {{ wrapper.survey.questions[answer.questionIndex].name }}
                    </b-col>
                    <b-col
                      sm="5"
                      class="d-inline"
                    >
                      <span
                        class="font-weight-medium"
                      >{{ $tk('ClientPortal.Forms.Survey.SelectedAnswer') }}:</span>
                      {{ wrapper.survey.questions[answer.questionIndex].answers[answer.answerIndex].answer }}
                    </b-col>
                    <b-col
                      sm="1"
                      class="text-right"
                    >
                      <span
                        class="d-flex justify-content-around  pt-50"
                        @click="removeSurveyQuestionConditionMapping(answerIndex, question)"
                      >
                        <b-button
                          size="sm"
                          variant="link"
                        >
                          <GjIcon
                            name="Delete"
                          />
                        </b-button>
                      </span>
                    </b-col>
                  </b-row>
                </div>
              </b-form-group>
              <b-form-row
                v-if="question.type > 0"
                class="mt-2"
              >
                <b-col
                  sm="5"
                >
                  <div>
                    <b-form-checkbox
                      v-model="question.endSurvey"
                      checked="false"
                      name="check-button"
                      class="d-inline m-25"
                      switch
                      inline
                      @change="handleCheckboxChange(question)"
                    >
                      {{ $tk('ClientPortal.Forms.Survey.EndSurvey') }}
                    </b-form-checkbox>
                    <GjIcon
                      v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.EndSurveyInfo')"
                      name="Info"
                    />
                  </div>
                </b-col>
                <div
                  v-if="question.endSurvey && question.answers.length > 0"
                >
                  <b-form-row
                    v-for="(answer, index) in question.answers"
                    :key="index"
                    class="mt-2"
                  >
                    <b-col
                      sm="2"
                      class="d-flex align-items-center justify-content-center"
                    >
                      <label>{{ answer.answer }}</label>
                    </b-col>
                    <b-col
                      sm="10"
                    >
                      <b-form-group
                        class="end-survey-message-group"
                      >
                        <label for="endSurveyMessage">{{ $tk('ClientPortal.Forms.Survey.EndSurveyMessage') }}</label>
                        <GjIcon
                          v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.EndSurveyInfo')"
                          name="Info"
                        />
                        <b-form-textarea
                          :id="'endSurveyMessage-' + index"
                          v-model="answer.endSurveyMessage"
                          name="endSurveyMessage"
                          :placeholder="$tk('ClientPortal.Forms.Survey.EndSurveyMessagePlaceholder')"
                          class="on-question-wrapper w-100"
                          style="resize: none;"
                        />
                      </b-form-group>
                      <b-form-group
                        class="end-survey-url-group"
                      >
                        <b-row>
                          <b-col
                            sm="6"
                            style="padding-right: 4px;"
                          >
                            <label for="endSurveyUrl">{{ $tk('ClientPortal.Forms.Survey.EndSurveyUrl') }}</label>
                            <GjIcon
                              v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.EndSurveyUrlInfo')"
                              name="Info"
                            />
                            <b-form-input
                              :id="'endSurveyUrl-' + index"
                              v-model="answer.endSurveyUrl"
                              name="endSurveyUrl"
                              placeholder="Enter end survey URL..."
                              class="on-question-wrapper w-100"
                            />
                          </b-col>
                          <b-col
                            sm="6"
                            style="padding-left: 4px;"
                          >
                            <label for="endImageUrl">End Image URL (optional)</label>
                            <GjIcon
                              v-b-tooltip.hover="$tk('ClientPortal.Forms.Survey.EndImageUrlInfo')"
                              name="Info"
                            />
                            <b-input-group>
                              <b-form-input
                                :id="'endImageUrl-' + index"
                                v-model="answer.endImageUrl"
                                name="endImageUrl"
                                placeholder="Enter end image URL..."
                                class="on-question-wrapper"
                              />
                              <b-input-group-append>
                                <b-dropdown
                                  right
                                  split-variant="outline-primary"
                                  variant="outline-secondary"
                                  class="format-dropdown"
                                  :class="{ 'no-arrow': answer.endImageFormat }"
                                >
                                  <template #button-content>
                                    <span v-if="answer.endImageFormat">{{ selectedFormatText(answer.endImageFormat) }}</span>
                                    <span
                                      v-else
                                      class="sr-only"
                                    >Toggle Dropdown</span>
                                  </template>
                                  <b-dropdown-item
                                    :class="{ 'selected-format': answer.endImageFormat === 1 }"
                                    @click="selectFormat(answer, 1)"
                                  >300x250</b-dropdown-item>
                                  <b-dropdown-item
                                    :class="{ 'selected-format': answer.endImageFormat === 2 }"
                                    @click="selectFormat(answer, 2)"
                                  >170x600</b-dropdown-item>
                                  <b-dropdown-item
                                    :class="{ 'selected-format': answer.endImageFormat === 3 }"
                                    @click="selectFormat(answer, 3)"
                                  >728x90</b-dropdown-item>
                                  <b-dropdown-item
                                    :class="{ 'selected-format': answer.endImageFormat === 4 }"
                                    @click="selectFormat(answer, 4)"
                                  >300x600</b-dropdown-item>
                                </b-dropdown>
                              </b-input-group-append>
                            </b-input-group>
                            <b-input-group-append>
                              <b-button
                                variant="outline-primary"
                                size="sm"
                                class="w-100 custom-mt-0-5"
                                @click.prevent="showGallery($event, answer)"
                              >
                                <GjIcon
                                  class="mr-50"
                                  name="Image"
                                />
                                {{ $tk('ClientPortal.Form.ChooseFromCreatives') }}
                              </b-button>
                            </b-input-group-append>
                          </b-col>
                          <span
                            v-if="answer.displayError"
                            class="text-danger"
                          >
                            {{ answer.displayError }}
                          </span>
                        </b-row>
                      </b-form-group>
                    </b-col>
                    <b-col cols="12">
                      <hr v-if="index < question.answers.length - 1">
                    </b-col>
                  </b-form-row>
                </div>
              </b-form-row>
            </b-tab>
          </b-tabs>
        </b-card>
        <b-button
          size="sm"
          class="bg-white mb-2"
          variant="outline-primary"
          @click="addSurveyQuestion"
        >
          <GjIcon
            class="mr-50"
            name="Plus"
          />
          {{ $tk('ClientPortal.Forms.Survey.AddQuestion') }}
        </b-button>
        <b-card
          class="mb-3"
          no-body
        >
          <b-card-title
            v-b-toggle.collapse-survey-preview
            class="d-flex justify-content-between p-2 mb-0"
            @click="previewSurvey"
          >
            <div class="text-primary d-flex align-items-center">
              <GjIcon
                class="mr-50"
                name="Show"
              />
              {{ $tk('ClientPortal.Forms.Survey.PreviewSurvey') }}
            </div>
            <span
              class="when-open"
            >
              <GjIcon
                name="ArrowUp"
              />
            </span>
            <span
              class="when-closed"
            >
              <GjIcon
                name="ArrowDown"
              />
            </span>
          </b-card-title>
          <b-collapse id="collapse-survey-preview">
            <b-form-group
              class="mb-0 px-2"
              :label="$tk('ClientPortal.Forms.Survey.ChooseFormat')"
            >
              <v-select
                v-model="wrapper.survey.previewAdFormatType"
                :clearable="false"
                label="name"
                :placeholder="$tk('ClientPortal.Survey.ChoosePreviewFormat')"
                :options="wrapper.adFormats"
                :reduce="format => format.id"
                @option:selected="updateSurveyPreview"
              />
            </b-form-group>
            <div class="p-2">
              <Preview
                v-if="previewingSurvey && !loadingPreview"
                :survey-wrapper-preview-model="surveyWrapperPreviewModel"
                :survey-configuration="configuration.surveyConfiguration"
              />
            </div>
          </b-collapse>
        </b-card>
      </b-container>
    </scroll-container>

    <b-modal
      id="gallery-modal"
      ref="galleryModal"
      body-class="modal-body--no-padding creatives-modal"
      hide-footer
      centered
      :title="$tk('ClientPortal.Form.Gallery')"
      size="xl"
    >
      <loader
        v-if="galleryLoading"
        :size="24"
        :show="true"
        class="gallery_loader"
      />
      <div v-else>
        <div class="d-block mx-1 mb-1">
          <b-form-input
            v-model="creativeSearchTerm"
            type="text"
            :placeholder="$tk('ClientPortal.Form.SearchCreative')"
            @input="searchCreative"
          />
        </div>
        <hr class="mb-0">
        <scroll-container
          class="pt-1 px-1"
          height-offset="260px"
        >
          <b-row class="mx-n50">
            <b-col
              v-for="(creativeItem, index) in creatives"
              :key="index"
              xl="2"
              md="3"
              sm="6"
              cols="12"
              class="mb-1 px-50"
            >
              <b-card
                class="mb-0 h-100 border"
                :class="{'border-primary' : creativeItem.selected}"
                @click.prevent="selectCreative(creativeItem)"
              >
                <div class="text-right">
                  <b-form-radio
                    v-if="isInEditMode"
                    v-model="creativeItem.selected"
                    value="true"
                    name="creative"
                  />
                  <b-form-checkbox
                    v-else
                    v-model="creativeItem.selected"
                    class="d-inline-block pl-1"
                  />
                </div>
                <b-card
                  class="mb-2"
                  body-class="p-0"
                >
                  <div
                    class="products-img text-center mx-auto"
                    :style="{ backgroundImage: 'url(' + creativeItem.url + ')'}"
                  />
                </b-card>
                <b-card-title class="mb-0">
                  {{ creativeItem.name }}
                </b-card-title>
                <b-card-text class="mb-0">
                  Size: {{ creativeItem.width }}x{{ creativeItem.height }}
                </b-card-text>
              </b-card>
            </b-col>
            <span
              v-if="creatives.length === 0"
              class="gallery-text"
            >
              <div>
                <GjIcon
                  size="64px"
                  name="Grid"
                />
              </div>
              <div>
                <span v-if="creativeSearchTerm.length > 0"> {{ $tk('ClientPortal.CompanionAds.NoCreativesAreFoundWithThatName') }}</span>
                <span v-else> {{ $tk('ClientPortal.CompanionAds.NoCreativesFound') }}</span>
              </div>
            </span>
          </b-row>
          <InfiniteLoading
            v-if="creatives.length > 0"
            spinner="spiral"
            @infinite="loadCreatives($event)"
          >
            <div
              slot="no-results"
            />
            <div
              slot="no-more"
            />
          </InfiniteLoading>
        </scroll-container>
        <div class="p-1 border-top text-right">
          <b-button
            class=""
            variant="outline-secondary"
            size="sm"
            @click.prevent="hideGallery"
          >
            {{ $tk('ClientPortal.Form.Actions.Cancel') }}
          </b-button>
          <b-button
            class="ml-50"
            variant="primary"
            size="sm"
            @click.prevent="addCreatives()"
          >
            {{ $tk('ClientPortal.Form.Add') }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <div class="border-top text-right py-1">
      <div class="container-small mx-auto">
        <b-button
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :to="`/${$route.params.accountId}/surveys`"
          :disabled="saving"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          class="bg-white ml-50"
          variant="primary"
          size="sm"
          :disabled="saving || !isFormComplete"
          @click="saveSurvey(wrapper.survey)"
        >
          {{ $tk('ClientPortal.Form.Actions.Save') }}
          <b-spinner
            v-if="saving"
            class="ml-50"
            variant="light"
            :label="$tk('ClientPortal.Forms.Survey.Spinning')"
            small
          />
        </b-button>
      </div>
    </div>
    <b-modal
      id="navigation-modal"
      ref="navigation-modal"
      centered
      :title="`Are you sure you want to navigate away?`"
    >
      <p>{{ modalMessage }}</p>
      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          @click="cancel()"
        >
          No
        </b-button>
        <b-button
          variant="danger"
          @click="confirm"
        >
          Yes
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import { AccountAccessManager } from '@/utils/accessManager/accessManager'
import Vue from 'vue'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import SurveysService from '@/api/client/surveys.service'
import GalleryService from '@/api/client/gallery.service'
import UploadService from '@/api/client/upload.service'
import ScrollContainer from '@/components/ScrollContainer.vue'
import AccountsService from '@/api/client/accounts.service'
import notification from '@/mixins/toastification'
import { mapGetters } from 'vuex'
import store from '@/store'
import vSelect from 'vue-select'
import scrollToError from '@/mixins/scrollToError'
import InfiniteLoading from 'vue-infinite-loading'
import SelectedItems from '@/components/SelectedItems.vue'
import AccountsInput from '@/components/AccountsInput.vue'
import Multiselect from 'vue-multiselect'
import loader from '@/components/Loader.vue'
import VSwatches from 'vue-swatches'
import Preview from './Preview.vue'
import 'vue-swatches/dist/vue-swatches.css'

const IMAGE_CREATIVE_TYPE = 1
const QUESTION_TYPES = Object.freeze({
  RADIO: 1,
  CHECKBOX: 2,
  RATING_SCALE: 3,
  TEXT: 4,
})

export default {
  components: {
    GjIcon,
    Preview,
    ScrollContainer,
    vSelect,
    SelectedItems,
    AccountsInput,
    InfiniteLoading,
    Multiselect,
    loader,
    VSwatches,
  },
  mixins: [notification],
  props: {
    surveyId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: true,
      wrapper: {},
      saving: false,
      showLogo: false,
      creatives: [],
      previewingSurvey: false,
      loadingPreview: false,
      galleryLoading: false,
      isInEditMode: true,
      creativeSearchTerm: '',
      errors: [],
      availableAccounts: [],
      ratingScaleOptions: [
        { id: 1, name: `1 ${this.$tk('ClientPortal.Pagination.To')} 5` },
        // { id: 2, name: `1 ${this.$tk('ClientPortal.Pagination.To')} 10` },
      ],
      surveyPreviewEntity: {},
      surveyWrapperPreviewModel: {},
      file: null,
      themeText: false,
      modalMessage: 'Are you sure you want to navigate away? Your changes will be lost.',
      selectedOption: undefined,
      accountAccessManager: AccountAccessManager,
      endImageFormats: [
        { id: 1, name: '300x250' },
        { id: 2, name: '170x600' },
        { id: 3, name: '728x90' },
        { id: 4, name: '300x600' },
      ],
      creativePage: 0,
      selectedAnswer: null,
      customizeColors: false,
      surveyColors: {
        backgroundColor: '#ffffff',
        titleColor: '#000000',
        optionTextColor: '#000000',
        optionBackgroundColor: '#ffffff',
        primaryButtonTextColor: '#ffffff',
        primaryButtonBackgroundColor: '#007bff',
        secondaryButtonTextColor: '#6c757d',
        secondaryButtonBackgroundColor: '#ffffff',
      },
      customSwatches: [
        '#FFFFFF', '#000000', '#F04D24', '#434CB0',
        '#3A81F6', '#B0E54C', '#3FE5B6', '#F4DC27',
        '#009688', '#4B4B4B', '#E34850', '#6310FA',
        '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
      ],
    }
  },
  computed: {
    isFormComplete() {
      const { name, themeId, questions } = this.wrapper.survey

      // If colors are customized, themeId can be null. If not customizing, themeId is required.
      const themeValidation = this.customizeColors || themeId

      return name && themeValidation && questions.length && questions.every(x => x.name && x.type)
    },
    isThemeSelected() {
      const { themeId } = this.wrapper.survey
      if (themeId) { return true }
      return false
    },
    getLogoFileName() {
      // If a new file has been selected, show its name
      if (this.file !== null) {
        return this.file.name
      }

      // Extract filename from existing logo URL
      if (this.wrapper.survey && this.wrapper.survey.logoImageUrl) {
        const url = this.wrapper.survey.logoImageUrl
        const filename = url.split('/').pop().split('?')[0]
        return decodeURIComponent(filename)
      }

      return ''
    },
    ...mapGetters(['configuration']),
  },
  watch: {
    '$route.params.accountId': 'fetchData',
  },
  async mounted() {
    this.fetchData()
  },
  methods: {
    getColorGuideTooltip(colorType) {
      // eslint-disable-next-line import/no-dynamic-require, global-require
      const imageUrl = require(`@/assets/images/survey-color-guides/${colorType}.svg`)
      return `<div style="text-align: center;"><img src="${imageUrl}" style="width: 200px; height: 140px; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); display: block;" /></div>`
    },
    async fetchData() {
      this.loading = true
      try {
        this.wrapper = await SurveysService.getSurvey(this.surveyId, this.$route.params.accountId)
        Vue.set(this.wrapper.survey, 'hasRewardCode', !!this.wrapper.survey.rewardCode)
        const accounts = await AccountsService.searchAccounts('', this.$route.params.accountId)
        this.availableAccounts = accounts.filter(x => x.id !== this.$route.params.accountId)

        // Check if survey has custom colors and populate them
        this.loadSurveyColors()

        this.initializeWatchers()
        this.updateEndSurveyStatus()
      } catch (err) {
        this.error(this.$tk('ClientPortal.Survey.Error.FaildToLoadSurvey'))
      } finally {
        this.loading = false
      }
    },

    checkAnswerErrors() {
      this.wrapper.survey.questions.forEach(question => {
        question.answers.forEach(answer => {
          if (answer.errors) {
            answer.displayError = answer.errors.EndImageFormat
              || answer.errors.EndImageUrl
              || answer.errors.EndSurveyMessage
              || answer.errors.EndSurveyUrl
              || null
          } else {
            answer.displayError = null
          }
        })
      })
    },

    addCreatives() {
      const selectedCreative = this.creatives.find(x => x.selected === true)
      if (selectedCreative && this.selectedAnswer) {
        this.selectedAnswer.endImageUrl = selectedCreative.url
      }
      this.hideGallery()
      this.resetGalleryState()
    },

    searchCreative() {
      Vue.set(this, 'creatives', [])
      this.creativePage = 0
      this.loadCreatives()
    },

    showGallery(event, answer) {
      event.stopPropagation()
      this.selectedAnswer = answer
      this.resetGalleryState()
      this.loadCreatives(null, true)
      this.$refs.galleryModal.show()
    },

    hideGallery() {
      this.$refs.galleryModal.hide()
    },

    resetGalleryState() {
      this.creativeSearchTerm = ''
      this.creatives.forEach(creative => {
        creative.selected = false
      })
      this.creativePage = 0
    },

    setEndImageUrl(url, answer) {
      if (answer) {
        answer.endImageUrl = url
      }
    },

    selectCreative(creative) {
      if (this.isInEditMode) {
        this.creatives.forEach(x => { x.selected = false })
      }
      creative.selected = !creative.selected
      if (creative.selected && this.selectedAnswer) {
        this.setEndImageUrl(creative.url, this.selectedAnswer)
      }
    },

    async loadCreatives($state, load = false) {
      if (load) { this.galleryLoading = true }
      const filterModel = {
        page: this.creativePage,
        type: IMAGE_CREATIVE_TYPE,
        searchTerm: this.creativeSearchTerm,
      }
      try {
        const response = await GalleryService.getCreatives(filterModel, this.$route.params.accountId)
        if (response.length) {
          this.creativePage += 1
          this.creatives.push(...response)
          if ($state) $state.loaded()
        } else if ($state) $state.complete()
      } catch (error) {
        throw new Error(error)
      }
      this.galleryLoading = false
    },

    selectFormat(answer, format) {
      answer.endImageFormat = format
    },
    selectedFormatText(format) {
      switch (format) {
        case 1:
          return '300x250'
        case 2:
          return '170x600'
        case 3:
          return '728x90'
        case 4:
          return '300x600'
        default:
          return ''
      }
    },
    watchEndImageUrl(answer) {
      this.$watch(() => answer.endImageUrl, newVal => {
        if (!newVal) {
          answer.endImageFormat = null
        }
      })
    },
    setSurveyTheme(themeId) {
      this.themeText = false
      Vue.set(this.wrapper.survey, 'themeId', themeId)
    },
    onCustomizeColorsToggle() {
      var surveyTheme = this.wrapper.themes.find(t => t.hidden === true)
      if (this.customizeColors) {
        // When color customization is enabled, set themeId to null
        Vue.set(this.wrapper.survey, 'themeId', surveyTheme.id)
        this.themeText = false
      } else {
        if (this.wrapper.survey.themeId === surveyTheme.id) {
          Vue.set(this.wrapper.survey, 'themeId', null)
        }

        if (!this.wrapper.survey.themeId) {
          this.themeText = true
          this.$nextTick(() => {
            this.scrollToThemeError()
          })
        }
      }
    },
    loadSurveyColors() {
      // Check if the survey has custom colors (any color property exists)
      const { survey } = this.wrapper
      if (survey.backgroundColor || survey.titleColor || survey.optionTextColor || survey.optionBackgroundColor
        || survey.primaryButtonTextColor || survey.primaryButtonBackgroundColor
        || survey.secondaryButtonTextColor || survey.secondaryButtonBackgroundColor) {
        this.customizeColors = true
        // Populate color values if they exist, otherwise keep defaults
        if (survey.backgroundColor) this.surveyColors.backgroundColor = survey.backgroundColor
        if (survey.titleColor) this.surveyColors.titleColor = survey.titleColor
        if (survey.optionTextColor) this.surveyColors.optionTextColor = survey.optionTextColor
        if (survey.optionBackgroundColor) this.surveyColors.optionBackgroundColor = survey.optionBackgroundColor
        if (survey.primaryButtonTextColor) this.surveyColors.primaryButtonTextColor = survey.primaryButtonTextColor
        if (survey.primaryButtonBackgroundColor) this.surveyColors.primaryButtonBackgroundColor = survey.primaryButtonBackgroundColor
        if (survey.secondaryButtonTextColor) this.surveyColors.secondaryButtonTextColor = survey.secondaryButtonTextColor
        if (survey.secondaryButtonBackgroundColor) this.surveyColors.secondaryButtonBackgroundColor = survey.secondaryButtonBackgroundColor
      }
    },

    async updateSurveyPreview() {
      // Create a copy of the survey object to avoid mutating the original state.
      const surveyForPreview = { ...this.wrapper.survey }

      // If color customization is enabled, add the colors to the preview object.
      // This is the key step to ensure colors are applied on format change.
      if (this.customizeColors) {
        surveyForPreview.backgroundColor = this.surveyColors.backgroundColor
        surveyForPreview.titleColor = this.surveyColors.titleColor
        surveyForPreview.optionTextColor = this.surveyColors.optionTextColor
        surveyForPreview.optionBackgroundColor = this.surveyColors.optionBackgroundColor
        surveyForPreview.primaryButtonTextColor = this.surveyColors.primaryButtonTextColor
        surveyForPreview.primaryButtonBackgroundColor = this.surveyColors.primaryButtonBackgroundColor
        surveyForPreview.secondaryButtonTextColor = this.surveyColors.secondaryButtonTextColor
        surveyForPreview.secondaryButtonBackgroundColor = this.surveyColors.secondaryButtonBackgroundColor
      }

      // Now call the existing surveyPreview method with the correctly prepared data.
      await this.surveyPreview(surveyForPreview)
    },
    getSurveyThemeClass(themeId) {
      if (this.themeText) {
        return 'border-danger'
      }
      return this.wrapper.survey.themeId === themeId ? 'border-primary' : ''
    },
    addSurveyQuestion() {
      if (this.configuration.surveyConfiguration.maxQuestions > this.wrapper.survey.questions.length) {
        const newQuestion = {
          id: 0,
          surveyId: 0,
          name: '',
          type: 1,
          // skippable: false,
          order: this.wrapper.survey.questions.length + 1,
          randomizedAnswers: false,
          allowOtherAnswers: false,
          conditionMappings: [],
          answers: [],
          errors: {},
        }

        this.wrapper.survey.questions.push(newQuestion)
        this.addDefaultAnswersForRadioAndCheckbox(newQuestion)
      } else {
        this.error(this.$tk('ClientPortal.Survey.Error.MaximumQuestionsReached'))
      }
    },
    addSurveyQuestionAnswer(question) {
      if (this.configuration.surveyConfiguration.maxAnswers > question.answers.length) {
        const answer = {
          id: 0,
          questionId: question.id,
          answer: '',
          order: question.answers.length + 1,
          description: '',
          endSurvey: false,
          endSurveyMessage: '',
          endSurveyUrl: '',
          endImageUrl: '',
          endImageFormat: null,
          errors: {},
        }
        question.answers.push(answer)
        this.watchEndImageUrl(answer)
        this.updateEndSurveyStatus()
      } else {
        this.error(this.$tk('ClientPortal.Survey.Error.MaximumAnswersReached'))
      }
    },
    addRatingScaleQuestionAnswers(question, fromNumber, toNumber) {
      if (question.id === 0) {
        question.hasRatingScaleAnswers = true
        if (question.answers.length > 0) { question.answers.splice(0, question.answers.length) }

        // eslint-disable-next-line no-plusplus
        for (let i = fromNumber; i < toNumber; i++) {
          question.answers.push({
            id: 0,
            answer: i + 1,
            description: '',
            questionId: 0,
          })
        }
      }
    },
    changeRatingScaleOption(ratingScaleOption, question) {
      if (ratingScaleOption === 1) {
        this.addRatingScaleQuestionAnswers(question, 0, 5)
      }
      // else {
      //   this.addRatingScaleQuestionAnswers(question, 0, 10)
      // }
    },
    async addSurveyAccountLogo() {
      if (this.wrapper.survey.logo == null) return

      this.file = this.wrapper.survey.logo

      const formData = new FormData()
      formData.append('file', this.file)

      try {
        const response = await UploadService.uploadImage(formData, this.$route.params.accountId)
        if (response.inserted) {
          this.wrapper.survey.logoImageUrl = response.imageLink
        }
      } catch {
        this.error(this.$tk('ClientPortal.Survey.Error.FailedToUploadImage'))
      }
    },
    removeLogo() {
      this.wrapper.survey.logo = null
      this.wrapper.survey.logoImageUrl = null
      this.file = null
      this.showLogo = false
    },
    removeSurveyQuestion(index, survey) {
      survey.questions.splice(index, 1)
    },
    removeSurveyQuestionAnswer(answerIndex, question, questionIndex) {
      var answerIsConditional = this.isAnswerConditional(answerIndex, questionIndex)
      if (answerIsConditional) {
        this.error(this.$tk('ClientPortal.Survey.Error.AnswerIsConditional'))
        return
      }
      question.answers.splice(answerIndex, 1)
    },
    isAnswerConditional(answerIndex, questionIndex) {
      for (let i = questionIndex; i < this.wrapper.survey.questions.length; i += 1) {
        const question = this.wrapper.survey.questions[i]
        for (let j = 0; j < question.conditionMappings.length; j += 1) {
          if (question.conditionMappings[j].questionIndex === questionIndex && question.conditionMappings[j].answerIndex === answerIndex) { return true }
        }
      }

      return false
    },
    removeAllSurveyQuestionConditionMapping(question) {
      if (!question.dependsOnAnswers) {
        question.conditionMappings = []
        if (question.conditionMappings.length === 0) { question.conditionalOperator = 0 }
      }
    },
    removeSurveyQuestionConditionMapping(index, question) {
      question.conditionMappings.splice(index, 1)
      if (question.conditionMappings.length === 0) { question.conditionalOperator = 0 }
    },
    changeSurveyQuestionType(surveyQuestionType, surveyQuestion) {
      switch (surveyQuestionType) {
        case QUESTION_TYPES.RADIO:
        case QUESTION_TYPES.CHECKBOX:
          this.addDefaultAnswersForRadioAndCheckbox(surveyQuestion)
          break
        case QUESTION_TYPES.RATING_SCALE:
          this.addRatingScaleQuestionAnswers(surveyQuestion)
          break
        case QUESTION_TYPES.TEXT:
          if (surveyQuestion.answers.length > 0) surveyQuestion.answers.splice(0, surveyQuestion.answers.length)
          break
        default: break
      }
    },
    addDefaultAnswersForRadioAndCheckbox(surveyQuestion) {
      if (surveyQuestion.id > 0) return
      if (surveyQuestion.hasRatingScaleAnswers) {
        surveyQuestion.answers.splice(0, surveyQuestion.answers.length)
        surveyQuestion.hasRatingScaleAnswers = false
      }

      if (surveyQuestion.answers.length > 0) return

      for (let i = 0; i < 2; i += 1) {
        const answer = {
          id: 0,
          surveyQuestionId: surveyQuestion.id,
          answer: '',
          description: '',
          errors: {},
        }
        surveyQuestion.answers.push(answer)
      }
    },
    addSurveySharedMapping(item) {
      this.wrapper.survey.surveySharedAccounts.push(item)
    },
    addSurveyPrivacyAccount(selectedItem) {
      if (this.wrapper.survey.surveySharedAccounts.some(x => x.accountId === selectedItem.id)) {
        return
      }

      const account = {
        id: 0,
        accountId: selectedItem.id,
        name: selectedItem.name,
        imageUrl: selectedItem.imageUrl,
      }
      this.addSurveySharedMapping(account)
    },
    clearSurveySharedAccounts() {
      this.wrapper.survey.surveySharedAccounts = []
    },
    removeSurveySharedItem(index) {
      this.wrapper.survey.surveySharedAccounts.splice(index, 1)
    },
    async searchAccounts(term) {
      try {
        const accounts = await AccountsService.searchAccounts(term, this.$route.params.accountId)
        this.availableAccounts = accounts.filter(x => x.id !== this.$route.params.accountId)
      } catch (err) {
        this.error(err.message)
      }
    },
    async saveSurvey(survey) {
      this.saving = true

      try {
        this.updateEndSurveyStatus()
        // Add color customization data to survey if colors are customized
        if (this.customizeColors) {
          survey.backgroundColor = this.surveyColors.backgroundColor
          survey.titleColor = this.surveyColors.titleColor
          survey.optionTextColor = this.surveyColors.optionTextColor
          survey.optionBackgroundColor = this.surveyColors.optionBackgroundColor
          survey.primaryButtonTextColor = this.surveyColors.primaryButtonTextColor
          survey.primaryButtonBackgroundColor = this.surveyColors.primaryButtonBackgroundColor
          survey.secondaryButtonTextColor = this.surveyColors.secondaryButtonTextColor
          survey.secondaryButtonBackgroundColor = this.surveyColors.secondaryButtonBackgroundColor
        }

        const { accountId } = this.$route.params
        const response = survey.id === 0 ? await SurveysService.createSurvey(survey, accountId) : await SurveysService.updateSurvey(survey, accountId)
        if (response.saved) {
          if (survey.id === 0) {
            store.commit('app/TOGGLE_NEW_CREATE_STATE', true)
          }
          this.success(response.message)
          this.$router.push(`/${accountId}/surveys`)
        } else {
          this.wrapper.survey = response.survey
          this.error(response.message)
          this.checkAnswerErrors()
        }
      } catch {
        this.error(this.$tk('ClientPortal.Survey.Error.FaildToSaveSurvey'))
      } finally {
        scrollToError(this.wrapper.survey.errors)
        this.saving = false
      }
    },
    getQuestionConditionMappingOptions(question, index) {
      var options = []
      for (let i = 0; i < this.wrapper.survey.questions.length; i += 1) {
        // eslint-disable-next-line no-continue
        if (i === index) { continue }
        if (i < index) {
          const tempQuestion = this.wrapper.survey.questions[i]
          if (tempQuestion.name !== '') {
            const optionGroupInfo = { label: tempQuestion.name, options: [] }
            for (let j = 0; j < tempQuestion.answers.length; j += 1) {
              const answer = tempQuestion.answers[j]
              optionGroupInfo.options.push({
                value: { questionIndex: i, answerIndex: j },
                text: answer.answer,
              })
            }
            options.push(optionGroupInfo)
          }
        }
      }
      question.conditionalQuestionAnswers = options
      return options
    },
    addQuestionConditionMapping(option, question) {
      const conditionMappingDoesNotExist = this.conditionMappingDoesNotExist(question, option)
      if (conditionMappingDoesNotExist) {
        const questionAnswer = {
          questionIndex: option.questionIndex,
          answerIndex: option.answerIndex,
        }
        question.conditionMappings.push(questionAnswer)
      }
    },
    conditionMappingDoesNotExist(question, option) {
      for (let i = 0; i < question.conditionMappings.length; i += 1) {
        const conditionMapping = question.conditionMappings[i]
        if (option.questionIndex === conditionMapping.questionIndex && option.answerIndex === conditionMapping.answerIndex) {
          return false
        }
      }
      return true
    },
    getQuestionAnswers(question) {
      const options = []
      for (let i = 0; i < question.answers.length; i += 1) {
        const answerOption = {
          value: i,
          text: question.answers[i].answer,
        }
        options.push(answerOption)
      }
      return options
    },
    handleCheckboxChange(question) {
      if (!question.endSurvey) {
        question.answers.forEach(answer => {
          answer.endSurvey = false
          answer.endSurveyMessage = null
          answer.endSurveyUrl = null
          answer.endImageUrl = null
          answer.endImageFormat = null
        })
      } else {
        question.answers.forEach(answer => {
          this.watchEndImageUrl(answer)
        })
      }
      this.updateEndSurveyStatus()
    },
    updateEndSurveyStatus() {
      this.wrapper.survey.questions.forEach(question => {
        question.answers.forEach(answer => {
          if (answer.endSurveyMessage || answer.endSurveyUrl || answer.endImageUrl) {
            answer.endSurvey = true
          }
        })
      })
    },
    initializeWatchers() {
      if (this.wrapper && this.wrapper.survey && Array.isArray(this.wrapper.survey.questions)) {
        this.wrapper.survey.questions.forEach(question => {
          if (question && Array.isArray(question.answers)) {
            question.answers.forEach(answer => {
              this.watchEndImageUrl(answer)
            })
          }
        })
      }
    },
    addAnswerEndSurvey(answerIndex, question) {
      const selectedOption = answerIndex.text
      for (let i = 0; i < question.answers.length; i += 1) {
        if (question.answers[i].answer === selectedOption) {
          question.answers[i].endSurvey = true
          this.selectedOption = i
        } else {
          question.answers[i].endSurvey = false
        }
      }
      // question.answers[selectedOption].endSurvey = true
    },
    removeAnswerEndingScreen(answerIndex, question) {
      question.answers[answerIndex].endSurvey = false
    },
    previewSurvey() {
      this.wrapper.survey.previewAdFormatType = 0

      // Check if theme is valid: either themeId is set or colors are customized
      const hasValidTheme = this.customizeColors || (this.wrapper.survey.themeId && this.wrapper.survey.themeId > 0)
      if (!hasValidTheme) {
        this.$root.$emit('bv::toggle::collapse', 'collapse-survey-preview')
        this.themeText = true
        this.$nextTick(() => {
          this.scrollToThemeError()
        })
      } else if (this.wrapper.survey.previewAdFormatType === 0 && this.wrapper.adFormats.length > 0) {
        this.previewingSurvey = !this.previewingSurvey
        this.wrapper.survey.previewAdFormatType = this.wrapper.adFormats[0].id

        // Create a copy of the survey and add custom colors if needed
        const surveyForPreview = { ...this.wrapper.survey }
        if (this.customizeColors) {
          surveyForPreview.backgroundColor = this.surveyColors.backgroundColor
          surveyForPreview.titleColor = this.surveyColors.titleColor
          surveyForPreview.optionTextColor = this.surveyColors.optionTextColor
          surveyForPreview.optionBackgroundColor = this.surveyColors.optionBackgroundColor
          surveyForPreview.primaryButtonTextColor = this.surveyColors.primaryButtonTextColor
          surveyForPreview.primaryButtonBackgroundColor = this.surveyColors.primaryButtonBackgroundColor
          surveyForPreview.secondaryButtonTextColor = this.surveyColors.secondaryButtonTextColor
          surveyForPreview.secondaryButtonBackgroundColor = this.surveyColors.secondaryButtonBackgroundColor
        }

        this.surveyPreview(surveyForPreview)
      }
    },
    async surveyPreview(model) {
      if (this.wrapper.adFormats.length === 0) {
        this.previewingSurvey = false
        return
      }
      if (!this.previewingSurvey) return
      this.loadingPreview = true
      try {
        this.surveyWrapperPreviewModel = await SurveysService.previewSurvey(model, this.$route.params.accountId)
        this.surveyWrapperPreviewModel.isOldVersion = !this.customizeColors
      } catch {
        this.error(this.$tk('ClientPortal.Survey.Error.FaildToLoadSurveyPreview'))
      }
      this.loadingPreview = false
    },
    resetClickableLink() {
      this.wrapper.survey.buttonText = ''
      this.wrapper.survey.destinationURL = ''
      this.wrapper.survey.errors.ButtonText = undefined
      this.wrapper.survey.errors.DestinationURL = undefined
    },

    resetRewardCode() {
      this.wrapper.survey.rewardCode = null
      this.wrapper.survey.rewardMessage = null
      this.wrapper.survey.isRewardUnique = false
      this.wrapper.survey.errors.RewardCode = undefined
      this.wrapper.survey.errors.RewardMessage = undefined
    },

    scrollToThemeError() {
      if (this.$refs.themeFormGroup && this.$refs.themeFormGroup.$el) {
        this.$refs.themeFormGroup.$el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },

    showConfirmationModal() {
      this.$refs['navigation-modal'].show()
      return new Promise(resolve => {
        this.$once('confirmed', () => {
          resolve()
        })
      })
    },
    confirm() {
      this.$emit('confirmed')
    },
    beforeRouteLeave(to, from, next) {
      const handlers = this.wrapper.survey.name
        || this.wrapper.survey.description
        || this.wrapper.survey.endImageDestinationURL
        || this.wrapper.survey.surveyPrivacyType === 2
        || this.wrapper.survey.surveyPrivacyType === 3
        || this.wrapper.survey.logo
        || this.wrapper.survey.showQuestionsNumber
        || this.wrapper.survey.hasClickableLink
        || this.wrapper.survey.questions.length
      // TODO: mi shtu edhe tjerat
      if (handlers && !this.saving) {
        this.showConfirmationModal().then(() => {
          next()
        }).catch(() => {
          next(false)
        })
      } else {
        next()
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/views/client/ads-manager/surveys/create-survey.scss';
@import '@/assets/scss/libs/_vue-multiselect.scss';
</style>
